import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, updateDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';

const UserContext = createContext();

export function useUser() {
    return useContext(UserContext);
}

export function UserProvider({ children }) {
    const { currentUser } = useAuth();
    const [userProfile, setUserProfile] = useState(null);
    const [tokens, setTokens] = useState(0);
    const [trainingStatus, setTrainingStatus] = useState('none');
    const [generationsCount, setGenerationsCount] = useState(0);

    // Real-time user data listener
    useEffect(() => {
        if (!currentUser) {
            setUserProfile(null);
            setTokens(0);
            setTrainingStatus('none');
            setGenerationsCount(0);
            return;
        }

        const userDocRef = doc(db, 'users', currentUser.uid);
        const unsubscribe = onSnapshot(userDocRef, (doc) => {
            if (doc.exists()) {
                const data = doc.data();
                setUserProfile(data);
                setTokens(data.tokens || 0);
                setTrainingStatus(data.trainingStatus || 'none');
                setGenerationsCount(data.generationsCount || 0);
            }
        });

        return () => unsubscribe();
    }, [currentUser]);

    const updateUserProfile = async (updates) => {
        if (!currentUser) return;

        try {
            const userDocRef = doc(db, 'users', currentUser.uid);
            await updateDoc(userDocRef, {
                ...updates,
                updatedAt: new Date()
            });
        } catch (error) {
            console.error('Error updating user profile:', error);
            throw error;
        }
    };

    const updateTokens = async (newTokenCount) => {
        if (!currentUser) return;

        try {
            await updateUserProfile({ tokens: newTokenCount });
        } catch (error) {
            console.error('Error updating tokens:', error);
            throw error;
        }
    };

    const updateTrainingStatus = async (status) => {
        if (!currentUser) return;

        try {
            await updateUserProfile({ trainingStatus: status });
        } catch (error) {
            console.error('Error updating training status:', error);
            throw error;
        }
    };

    const incrementGenerations = async () => {
        if (!currentUser) return;

        try {
            const newCount = generationsCount + 1;
            await updateUserProfile({ 
                generationsCount: newCount,
                tokens: Math.max(0, tokens - 1) // Deduct 1 token
            });
        } catch (error) {
            console.error('Error incrementing generations:', error);
            throw error;
        }
    };

    const value = {
        userProfile,
        tokens,
        trainingStatus,
        generationsCount,
        updateUserProfile,
        updateTokens,
        updateTrainingStatus,
        incrementGenerations
    };

    return (
        <UserContext.Provider value={value}>
            {children}
        </UserContext.Provider>
    );
}